import React, { useState, useEffect } from 'react';
import { PageHeader, SubTabBar, fmtDollar, fmtPct, fmtCount, fmtMillions } from './shared';
import { apiFetch } from '../api';

// ── Population fractions (each group segments the full membership) ────────────

function fmtRange(low, high) {
  return `${fmtDollar(low)} – ${fmtDollar(high)}`;
}

function isTextSelected() {
  const sel = window.getSelection();
  return sel && sel.toString().length > 0;
}

function fmtTotalRange(low, high) {
  return `${fmtMillions(low)} – ${fmtMillions(high)}`;
}

const POP_FRACTIONS = {
  'Medicare Advantage': 0.200,
  'Premium+ PPO':       0.250,
  'Standard HMO':       0.300,
  'Value EPO':          0.150,
  'Medicaid Managed':   0.100,
  'Northeast':          0.300,
  'West':               0.250,
  'Southeast':          0.250,
  'Midwest':            0.200,
  'Age 65+':            0.150,
  'Age 50-64':          0.250,
  'Age 35-49':          0.300,
  'Age 18-34':          0.300,
};

function popMembers(name, memberCount) {
  return Math.round((memberCount ?? 0) * (POP_FRACTIONS[name] ?? 0));
}

function annualSavings(pmpm, members) {
  return pmpm * members * 12;
}

// ── Initiative Detail ──────────────────────────────────────────────────────────────

const CALCULATION_PLACEHOLDER = `This initiative is derived from adjudicated medical and pharmacy claims data spanning a rolling 12-month period. ` +
  `Claims are normalized to a per-member-per-month (PMPM) basis using the eligible member months in each reporting period. ` +
  `Opportunity ranges reflect low, mid, and high intervention scenarios modeled against peer benchmark cohorts and internal trend data. ` +
  `Realized savings projections apply intervention capture rates of 10%, 15%, and 20% to the mid PMPM opportunity. ` +
  `A more targeted methodology specific to this initiative will be added in a future release.`;

function CalculationSection({ metric }) {
  return (
    <div className="card mb-3">
      <div className="card-header fw-semibold d-flex align-items-center gap-2">
        How This Initiative Is Calculated
        <span className="badge bg-secondary" style={{ fontSize: '0.72rem', fontWeight: 400 }}>{metric.module}</span>
      </div>
      <div className="card-body">
        <p style={{ fontSize: '0.88rem', margin: 0, color: 'var(--eh-text-mid)' }}>{metric.method ?? CALCULATION_PLACEHOLDER}</p>
      </div>
    </div>
  );
}

function SavingsByPopulation({ metric, populations, memberCount }) {
  return (
    <div className="card">
      <div className="card-header fw-semibold">Anticipated Savings by Population</div>
      <div className="card-body p-0">
        <div className="overflow-auto">
          <table className="table table-sm mb-0 dash-table">
            <thead>
              <tr>
                <th>Population</th>
                <th className="text-end">Members</th>
                <th className="text-end">Annual Savings (Mid)</th>
                <th className="text-end">Annual Savings (High)</th>
              </tr>
            </thead>
            <tbody>
              {populations.map(group => (
                <React.Fragment key={group.group}>
                  <tr style={{ background: '#f0f4f8' }}>
                    <td colSpan={4} style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--eh-text-mid)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '0.35rem 0.75rem' }}>
                      {group.group}
                    </td>
                  </tr>
                  {group.populations.map(pop => {
                    const members = popMembers(pop.name, memberCount);
                    return (
                      <tr key={pop.name}>
                        <td>{pop.name}</td>
                        <td className="text-end">{fmtCount(members)}</td>
                        <td className="text-end fw-semibold">{fmtMillions(annualSavings(metric.pmpmMid, members))}</td>
                        <td className="text-end">{fmtMillions(annualSavings(metric.pmpmHigh, members))}</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function InitiativeDetail({ metric, populations, memberCount, onBack }) {
  return (
    <div style={{ maxWidth: 860 }}>
      <button className="btn btn-link btn-sm p-0 mb-3" style={{ color: 'var(--eh-blue)' }} onClick={onBack}>
        ← Back to initiatives
      </button>
      <h5 className="mb-1" style={{ color: 'var(--eh-navy)' }}>{metric.name}</h5>
      <p className="mb-3" style={{ fontSize: '0.85rem', color: 'var(--eh-text-mid)' }}>
        <span className="fw-semibold" style={{ color: 'var(--eh-text-dark)' }}>Savings Range: </span>
        {fmtDollar(metric.pmpmLow)} low · <strong>{fmtDollar(metric.pmpmMid)}</strong> mid · {fmtDollar(metric.pmpmHigh)} high PMPM
        &nbsp;&nbsp;·&nbsp;&nbsp;{metric.notes}
      </p>
      <CalculationSection metric={metric} />
      <SavingsByPopulation metric={metric} populations={populations} memberCount={memberCount} />
    </div>
  );
}

// ── Shared initiatives table ───────────────────────────────────────────────────

function InitiativesTable({ metrics, onSelectInitiative }) {
  const totals = metrics.reduce(
    (acc, m) => ({
      pmpmLow:            acc.pmpmLow            + m.pmpmLow,
      pmpmHigh:           acc.pmpmHigh           + m.pmpmHigh,
      realizedLow:        acc.realizedLow        + m.realizedLow,
      realizedHigh:       acc.realizedHigh       + m.realizedHigh,
      realizedTotalLow:   acc.realizedTotalLow   + m.realizedTotalLow,
      realizedTotalHigh:  acc.realizedTotalHigh  + m.realizedTotalHigh,
      pctOfOpportunity:   acc.pctOfOpportunity   + m.pctOfOpportunity,
    }),
    { pmpmLow: 0, pmpmHigh: 0, realizedLow: 0, realizedHigh: 0, realizedTotalLow: 0, realizedTotalHigh: 0, pctOfOpportunity: 0 }
  );

  return (
    <div className="overflow-auto">
      <table className="table table-sm mb-0 dash-table ini-initiatives-table w-100">
        <thead>
          <tr>
            <th>Initiative</th>
            <th>Module</th>
            <th className="text-end">Opportunity Range (PMPM)</th>
            <th className="text-end">Realized Range (PMPM)</th>
            <th className="text-end">Realized Range (Total)</th>
            <th className="text-end">Realized Range (% of Total Spend)</th>
          </tr>
        </thead>
        <tbody>
          {metrics.map(m => (
            <tr key={m.name} className="ini-initiative-row" onClick={() => { if (!isTextSelected()) onSelectInitiative(m); }}>
              <td className="ini-initiative-name">{m.name}</td>
              <td style={{ color: 'var(--eh-text-mid)' }}>{m.module}</td>
              <td className="text-end">{fmtRange(m.pmpmLow, m.pmpmHigh)}</td>
              <td className="text-end">{fmtRange(m.realizedLow, m.realizedHigh)}</td>
              <td className="text-end">{fmtTotalRange(m.realizedTotalLow, m.realizedTotalHigh)}</td>
              <td className="text-end fw-semibold">{fmtPct(m.pctOfOpportunity, 1)}</td>
            </tr>
          ))}
          <tr style={{ background: 'var(--eh-navy)', color: '#fff', fontWeight: 700 }}>
            <td colSpan={2}>Total</td>
            <td className="text-end">{fmtRange(totals.pmpmLow, totals.pmpmHigh)}</td>
            <td className="text-end">{fmtRange(totals.realizedLow, totals.realizedHigh)}</td>
            <td className="text-end">{fmtTotalRange(totals.realizedTotalLow, totals.realizedTotalHigh)}</td>
            <td className="text-end">{fmtPct(totals.pctOfOpportunity, 1)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── Summary view ───────────────────────────────────────────────────────────────

function CategorySummaryCard({ category, onSelectInitiative, onViewAll }) {
  const top3 = [...category.initiatives].sort((a, b) => b.pmpmMid - a.pmpmMid).slice(0, 3);
  const totals = top3.reduce(
    (acc, m) => ({
      pmpmLow: acc.pmpmLow + m.pmpmLow,
      pmpmHigh: acc.pmpmHigh + m.pmpmHigh,
      realizedLow: acc.realizedLow + m.realizedLow,
      realizedHigh: acc.realizedHigh + m.realizedHigh,
      realizedTotalLow: acc.realizedTotalLow + m.realizedTotalLow,
      realizedTotalHigh: acc.realizedTotalHigh + m.realizedTotalHigh,
      pctOfOpportunity: acc.pctOfOpportunity + m.pctOfOpportunity,
    }),
    { pmpmLow: 0, pmpmHigh: 0, realizedLow: 0, realizedHigh: 0, realizedTotalLow: 0, realizedTotalHigh: 0, pctOfOpportunity: 0 }
  );

  return (
    <div className="card h-100">
      <div className="card-header d-flex justify-content-between align-items-center">
        <span style={{ fontSize: '0.82rem' }}>{category.category}</span>
      </div>
      <div className="card-body p-0">
        <table className="table table-sm mb-0 dash-table ini-initiatives-table w-100">
          <thead>
            <tr>
              <th>Top Initiative</th>
              <th className="text-end">Opportunity Range (PMPM)</th>
              <th className="text-end">Realized Range (PMPM)</th>
              <th className="text-end">Realized Range (Total)</th>
              <th className="text-end">Realized Range (% of Total Spend)</th>
            </tr>
          </thead>
          <tbody>
            {top3.map(m => (
              <tr key={m.name} className="ini-initiative-row" onClick={() => { if (!isTextSelected()) onSelectInitiative(m); }}>
                <td className="ini-initiative-name">{m.name}</td>
                <td className="text-end">{fmtRange(m.pmpmLow, m.pmpmHigh)}</td>
                <td className="text-end">{fmtRange(m.realizedLow, m.realizedHigh)}</td>
                <td className="text-end">{fmtTotalRange(m.realizedTotalLow, m.realizedTotalHigh)}</td>
                <td className="text-end fw-semibold">{fmtPct(m.pctOfOpportunity, 1)}</td>
              </tr>
            ))}
            <tr style={{ background: 'var(--eh-navy)', color: '#fff', fontWeight: 700 }}>
              <td>Top 3 Total</td>
              <td className="text-end">{fmtRange(totals.pmpmLow, totals.pmpmHigh)}</td>
              <td className="text-end">{fmtRange(totals.realizedLow, totals.realizedHigh)}</td>
              <td className="text-end">{fmtTotalRange(totals.realizedTotalLow, totals.realizedTotalHigh)}</td>
              <td className="text-end">{fmtPct(totals.pctOfOpportunity, 1)}</td>
            </tr>
          </tbody>
        </table>
        <div className="p-2 text-end">
          <button className="btn btn-link btn-sm p-0" style={{ fontSize: '0.8rem' }} onClick={onViewAll}>
            View all {category.leverCount} initiatives →
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryView({ categories, memberCount, onSelectInitiative, onViewCategory }) {
  const allInitiatives = categories.flatMap(c => c.initiatives);
  const totals = allInitiatives.reduce(
    (acc, m) => ({
      pmpmLow:      acc.pmpmLow      + m.pmpmLow,
      pmpmHigh:     acc.pmpmHigh     + m.pmpmHigh,
      realizedLow:  acc.realizedLow  + m.realizedLow,
      realizedHigh: acc.realizedHigh + m.realizedHigh,
    }),
    { pmpmLow: 0, pmpmHigh: 0, realizedLow: 0, realizedHigh: 0 }
  );
  const detail = `Total Opportunity ${fmtRange(totals.pmpmLow, totals.pmpmHigh)} PMPM  ·  Total Realized ${fmtRange(totals.realizedLow, totals.realizedHigh)} PMPM`;

  return (
    <>
      <PageHeader title="Recommended Initiatives  ·  Summary" memberCount={memberCount} detail={detail} />
      <div className="row g-3 pb-4">
        {categories.map(cat => (
          <div key={cat.category} className="col-12 col-xl-6">
            <CategorySummaryCard
              category={cat}
              onSelectInitiative={onSelectInitiative}
              onViewAll={() => onViewCategory(cat.category)}
            />
          </div>
        ))}
      </div>
    </>
  );
}

// ── Category view ──────────────────────────────────────────────────────────────

function CategoryView({ category, memberCount, onSelectInitiative }) {
  return (
    <>
      <PageHeader title={`Recommended Initiatives  ·  ${category.category}`} memberCount={memberCount} />
      <div className="card">
        <div className="card-header">
          <span>{category.leverCount} initiatives</span>
        </div>
        <div className="card-body p-0">
        <InitiativesTable metrics={category.initiatives} onSelectInitiative={onSelectInitiative} />
        </div>
      </div>
    </>
  );
}

// ── Root component ─────────────────────────────────────────────────────────────

function categorySlug(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function buildSubTabs(categories) {
  return [
    { key: 'summary', label: 'Summary' },
    ...categories.map(c => ({ key: categorySlug(c.category), label: c.category })),
  ];
}

export default function RecommendedInitiatives() {
  const [categories, setCategories]     = useState(null);
  const [populations, setPopulations]   = useState(null);
  const [memberCount, setMemberCount]   = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('summary');
  const [selectedInitiative, setSelectedInitiative] = useState(null);

  useEffect(() => {
    apiFetch('/api/initiatives/metrics').then(r => r.json()).then(setCategories).catch(() => setCategories([]));
    apiFetch('/api/initiatives/populations').then(r => r.json()).then(setPopulations).catch(() => setPopulations([]));
    apiFetch('/api/dashboards/intelligence-config').then(r => r.json()).then(d => setMemberCount(d.membership_count)).catch(() => {});
  }, []);

  if (!categories || !populations) return <div className="p-4 text-muted">Loading…</div>;

  const subTabs = buildSubTabs(categories);

  const handleSubTabChange = (key) => {
    setActiveSubTab(key);
    setSelectedInitiative(null);
  };

  const handleSelectInitiative = (initiative) => {
    setSelectedInitiative(initiative);
  };

  const handleBack = () => setSelectedInitiative(null);

  const activeCategory = categories.find(c => categorySlug(c.category) === activeSubTab);

  const renderContent = () => {
    if (selectedInitiative) {
      return (
        <InitiativeDetail
          metric={selectedInitiative}
          populations={populations}
          memberCount={memberCount}
          onBack={handleBack}
        />
      );
    }
    if (activeSubTab === 'summary') {
      return (
        <SummaryView
          categories={categories}
          memberCount={memberCount}
          onSelectInitiative={handleSelectInitiative}
          onViewCategory={(name) => handleSubTabChange(categorySlug(name))}
        />
      );
    }
    if (activeCategory) {
      return (
        <CategoryView
          category={activeCategory}
          memberCount={memberCount}
          onSelectInitiative={handleSelectInitiative}
        />
      );
    }
    return null;
  };

  return (
    <div className="dash-tab-content">
      <SubTabBar tabs={subTabs} active={activeSubTab} onChange={handleSubTabChange} />
      {renderContent()}
    </div>
  );
}
